import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { MeResponse } from '../types';
import { authApi } from '../api/auth';
import { AUTH_FRONT_ORIGIN } from '../config/env';

interface AuthContextType {
  user: MeResponse | null;
  loading: boolean;
  isPendingApproval: boolean;
  login: () => Promise<MeResponse | null>;
  logout: () => void;
  refreshUser: () => Promise<MeResponse | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const isPublicAuthPath =
    window.location.pathname.includes('/login') ||
    window.location.pathname.includes('/signup') ||
    window.location.pathname.includes('/register') ||
    window.location.pathname.includes('/reset-password');

  const refreshUser = useCallback(async (): Promise<MeResponse | null> => {
    try {
      const response = await authApi.getMe();
      if (response.data.isSuccess && response.data.result) {
        setUser(response.data.result);
        return response.data.result;
      } else {
        setUser(null);
        return null;
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        try {
          const reissue = await authApi.reissue();
          const newAccessToken = reissue.data.result?.accessToken;
          if (newAccessToken) localStorage.setItem('accessToken', newAccessToken);
          const retry = await authApi.getMe();
          if (retry.data.isSuccess && retry.data.result) {
            setUser(retry.data.result);
            return retry.data.result;
          }
        } catch (reissueError) {
          setUser(null);
        }
      } else {
        setUser(null);
      }

      if (!isPublicAuthPath) {
        const redirectUri = encodeURIComponent(window.location.href);
        window.location.href = `${AUTH_FRONT_ORIGIN}/login?redirect_uri=${redirectUri}`;
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [isPublicAuthPath]);

  const login = async (): Promise<MeResponse | null> => {
    return refreshUser();
  };

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout request failed', error);
    }
    localStorage.removeItem('accessToken');
    setUser(null);
    window.location.href = `${AUTH_FRONT_ORIGIN}/login`;
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  return (
    <AuthContext.Provider value={{ user, loading, isPendingApproval: user?.approvalStatus === 'PENDING', login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
